'use client'

import { memo, useEffect, useRef, useState } from 'react'
import ImageWithFallback from 'src/components/ImageWithFallBack'
import { logo1Interface, logos1 } from 'src/utils/data'

// CSS-based animation configuration
const ON_HOVER_PAUSE = true

const LogoCard = memo(({ logo }: { logo: logo1Interface }) => {
  return (
    <div className="inline-flex mx-4 flex-shrink-0 group">
      <div
        className={`relative flex flex-col items-center justify-center w-[140px] md:w-[180px] border border-gray-200 rounded-md shadow-sm  hover:shadow-md transition-all hover:-translate-y-1 ${
          logo.caseStudyLink
            ? 'h-[140px] sm:h-[160px] shadow-lg'
            : 'h-[120px] sm:h-[140px]'
        }`}>
        {logo.caseStudyLink ? (
          <a href={logo.caseStudyLink} target="_self" rel="noopener noreferrer">
            <div className="flex items-center justify-center bg-white rounded-lg p-2 h-20 md:h-24 w-32">
              <ImageWithFallback
                src={`/img/logos/${logo.imgName}`}
                alt={`${logo.alt} logo`}
                width={120}
                height={90}
                className="w-auto max-h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                loading="lazy"
              />
            </div>

            <div className="my-1.5 md:my-2">
              <div
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 bg-[#196E82] text-white px-2 sm:px-2.5 py-0.5 md:px-6 md:py-1.5 rounded-2xl text-[10px] sm:text-xs font-medium hover:bg-[#196E82]/90 transition-all shadow-sm">
                Case study →
              </div>
            </div>
          </a>
        ) : (
          <div className="flex items-center justify-center bg-white rounded-lg p-2 h-20 md:h-24 w-32">
            <ImageWithFallback
              src={`/img/logos/${logo.imgName}`}
              alt={`${logo.alt} logo`}
              width={120}
              height={90}
              className="w-auto max-h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </div>
  )
})

LogoCard.displayName = 'LogoCard'

const LogoRow = memo(
  ({
    logos,
    reverseDirection = false
  }: {
    logos: logo1Interface[]
    reverseDirection?: boolean
  }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isPaused, setIsPaused] = useState(false)

    // Calculate animation duration based on number of logos
    const animationDuration = logos.length * 10 // Adjust this multiplier as needed for speed

    // Handle manual pause on hover
    const handleMouseEnter = () => ON_HOVER_PAUSE && setIsPaused(true)
    const handleMouseLeave = () => setIsPaused(false)

    return (
      <div
        ref={containerRef}
        className="relative overflow-hidden will-change-transform"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <div className="absolute left-0 top-0 w-[5%] h-full z-10 bg-gradient-to-r from-white to-transparent" />

        <div className="overflow-hidden">
          <div
            className={`flex flex-nowrap min-h-[140px] sm:min-h-[160px] items-center ${
              isPaused ? 'animate-pause' : ''
            }`}
            style={{
              animationName: reverseDirection ? 'scroll-rtl' : 'scroll-ltr',
              animationDuration: `${animationDuration}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              animationPlayState: isPaused ? 'paused' : 'running',
              willChange: 'transform',
              width: 'fit-content'
            }}>
            {/* Original logos */}
            {logos.map((logo, idx) => (
              <LogoCard key={`${logo.imgName}-${idx}`} logo={logo} />
            ))}

            {/* Duplicate logos for seamless scrolling */}
            {logos.map((logo, idx) => (
              <LogoCard key={`${logo.imgName}-clone-${idx}`} logo={logo} />
            ))}
          </div>
        </div>

        <div className="absolute right-0 top-0 w-[5%] h-full z-10 bg-gradient-to-l from-white to-transparent" />
      </div>
    )
  }
)

LogoRow.displayName = 'LogoRow'

const TrustedBySection = () => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Add keyframe animations to document once when component mounts
    if (
      typeof document !== 'undefined' &&
      !document.getElementById('scroll-animations')
    ) {
      const styleSheet = document.createElement('style')
      styleSheet.id = 'scroll-animations'
      styleSheet.textContent = `
        @keyframes scroll-ltr {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        @keyframes scroll-rtl {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }

        .animate-pause {
          animation-play-state: paused !important;
        }
      `
      document.head.appendChild(styleSheet)
    }

    setMounted(true)
    return () => {
      setMounted(false)
      // Clean up animation styles on unmount
      if (typeof document !== 'undefined') {
        const styleEl = document.getElementById('scroll-animations')
        if (styleEl) styleEl.remove()
      }
    }
  }, [])

  if (!mounted) return null

  // Pre-calculate the row logos
  const logoRowCount = Math.ceil(logos1.length / 3)
  const row1Logos = logos1.slice(0, logoRowCount)
  const row2Logos = logos1.slice(logoRowCount, logoRowCount * 2)
  const row3Logos = logos1.slice(logoRowCount * 2)

  return (
    <section className="py-6 md:py-10 lg:py-16 bg-white relative overflow-hidden z-10 pb-10">
      <div className="container mx-auto px-3 md:px-8">
        <h2 className="text-center text-xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-5 md:mb-8 lg:mb-24 text-[#0a1734]">
          Trusted by 200+ Restaurant Chains
        </h2>

        <div className="flex flex-col gap-2 mb-20 md:mb-40 relative">
          <LogoRow logos={row1Logos} />
          <LogoRow logos={row2Logos} reverseDirection={true} />
          <LogoRow logos={row3Logos} />
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 w-full h-44 bg-repeat-x -z-0 mb-10"
        style={{
          backgroundImage:
            'url(/v2/images/deliveryIntelligenceSectionBackdrop.svg)',
          backgroundPosition: 'center bottom'
        }}
      />
    </section>
  )
}

export default TrustedBySection
